import { createFileRoute } from '@tanstack/react-router'
import { cx } from 'cva'
import { type ComponentType, type ReactNode } from 'react'
import { Button, Hash } from 'regen-ui'
import { useConnect, useConnection } from 'wagmi'
import ArrowDownLeft from '~icons/lucide/arrow-down-left'
import ArrowUpRight from '~icons/lucide/arrow-up-right'
import Landmark from '~icons/lucide/landmark'
import SendHorizontal from '~icons/lucide/send-horizontal'
import ShieldCheck from '~icons/lucide/shield-check'

import { useDialogConnector } from '#/lib/connectors.js'
import { TempoLogo, TempoMarkBoxed } from '#/ui/TempoLogo.js'

/** Public welcome route. */
export const Route = createFileRoute('/welcome')({
  component: WelcomePage,
  head: () => ({
    links: [{ href: 'https://wallet.tempo.xyz/', rel: 'canonical' }],
  }),
})

const linkClassName =
  'rounded-button text-foreground-secondary hover:text-foreground active:text-foreground active:opacity-45 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
const emphasizedLinkClassName =
  'rounded-button text-foreground underline decoration-border underline-offset-4 hover:opacity-75 hover:decoration-current active:opacity-45 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'

const actions = [
  {
    icon: SendHorizontal,
    label: 'Send',
    text: 'Move funds on Tempo.',
  },
  {
    icon: ArrowDownLeft,
    label: 'Receive',
    text: 'Share your wallet address.',
  },
  {
    icon: Landmark,
    label: 'Deposit',
    text: 'Add funds with supported rails.',
  },
  {
    icon: ShieldCheck,
    label: 'Zones',
    text: 'Manage private balances on Tempo.',
  },
] as const

export function WelcomePage() {
  const connected = !!useConnection().address

  return (
    <main className="relative isolate min-h-dvh min-w-[360px] overflow-hidden bg-background text-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(var(--border-color-border) 1px, transparent 1px), linear-gradient(90deg, var(--border-color-border) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <WelcomeHeader connected={connected} />

      <section className="mx-auto grid min-h-[calc(100dvh-160px)] w-full max-w-[1180px] grid-cols-1 items-center gap-8 px-4 py-10 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(340px,450px)] lg:gap-14 lg:py-16">
        <div className="flex min-w-0 flex-col gap-7">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4">
              <h1 className="text-heading-48 font-['Pilat_Book',ui-sans-serif,system-ui,sans-serif] font-normal tracking-[-0.02em] text-foreground lg:text-heading-64">
                Your wallet <br className="hidden xl:block" />
                for Tempo
              </h1>
              <p className="max-w-[560px] text-copy-16 text-foreground-secondary">
                A first-party wallet for Tempo balances, transfers, deposits, private balances, and
                everyday account control.
              </p>
            </div>
          </div>

          <WalletActionGrid />
          <p className="text-label-14 text-foreground-secondary">
            Built for{' '}
            <a
              className={emphasizedLinkClassName}
              href="https://tempo.xyz"
              rel="noopener noreferrer"
              target="_blank"
            >
              Tempo
            </a>
            , with passkey sign-in and account controls in one place.
          </p>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <WalletPreview />
          <DocsLink />
        </div>
      </section>

      <WelcomeFooter />
    </main>
  )
}

function WelcomeHeader(props: { connected: boolean }) {
  const { connected } = props
  const connector = useDialogConnector()
  const connect = useConnect()

  return (
    <header className="relative z-10">
      <div className="mx-auto flex max-w-[1180px] items-center gap-4 px-4 py-5 sm:px-8">
        <a
          aria-label="Tempo home"
          className="text-foreground hover:opacity-75 active:opacity-45 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
          href="/"
        >
          <TempoLogo className="h-[18px] w-auto" />
        </a>

        <div className="ml-auto flex items-center gap-4">
          <nav aria-label="Landing" className="hidden items-center justify-end gap-1 sm:flex">
            <HeaderLink href="https://docs.tempo.xyz">Docs</HeaderLink>
            <HeaderLink href="/support">Support</HeaderLink>
          </nav>

          <div aria-hidden className="hidden h-4 border-l border-border sm:block" />

          {connected ? (
            <Button render={<a href="/" />} variant="primary">
              Wallet
            </Button>
          ) : (
            <Button
              icon={<TempoMarkBoxed className="h-[18px] w-[18px]" />}
              loading={connect.isPending}
              onClick={() => connect.mutate({ connector })}
              variant="primary"
            >
              Sign in with Tempo
            </Button>
          )}
        </div>
      </div>
    </header>
  )
}

function HeaderLink(props: { children: ReactNode; href: string }) {
  const { children, href } = props
  const external = href.startsWith('https://')
  return (
    <a
      className={cx(linkClassName, 'px-3 py-1.5 text-label-14')}
      href={href}
      rel={external ? 'noopener noreferrer' : undefined}
      target={external ? '_blank' : undefined}
    >
      {children}
    </a>
  )
}

function WalletActionGrid() {
  return (
    <div className="grid max-w-[560px] gap-3 sm:grid-cols-2">
      {actions.map((action) => (
        <div className="rounded-body border border-border bg-surface p-4" key={action.label}>
          <div className="flex items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-identicon bg-secondary text-foreground-secondary">
              <action.icon className="size-4" />
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <p className="text-label-14 text-foreground">{action.label}</p>
              <p className="text-label-13 text-foreground-secondary">{action.text}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function WalletPreview() {
  return (
    <div className="overflow-hidden rounded-body border border-border bg-surface shadow-[0_24px_80px_rgb(0_0_0/0.08)]">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex flex-col gap-1">
          <p className="text-label-13 text-foreground-secondary">Tempo Wallet</p>
          <p className="text-heading-24 text-foreground">$12,840.32</p>
        </div>
        <span className="flex size-10 items-center justify-center rounded-identicon bg-accent text-on-accent">
          <TempoMarkBoxed className="size-5" />
        </span>
      </div>

      <div className="flex flex-col gap-4 p-5">
        <div className="grid grid-cols-2 gap-2">
          <PreviewButton icon={SendHorizontal} label="Send" />
          <PreviewButton icon={Landmark} label="Deposit" />
        </div>

        <div className="flex flex-col gap-2 rounded-body bg-pane p-3.5">
          <PreviewRow label="PathUSD" value="$8,420.00" />
          <PreviewRow label="Private balance" value="$1,250.00" />
          <PreviewRow label="Recent activity" value="12 transfers" />
        </div>

        <div className="flex items-center gap-3 rounded-body border border-border bg-surface px-3 py-2.5">
          <ShieldCheck className="size-4 shrink-0 text-foreground-secondary" />
          <div className="min-w-0 flex-1">
            <p className="text-label-13 text-foreground">Access controls ready</p>
            <p className="truncate text-label-12 text-foreground-secondary">
              <Hash value="0x0b2c69c5c1bbd3f8b642c8d8a3c8a903f780f9b2" />
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function PreviewButton(props: {
  icon: ComponentType<{ className?: string | undefined }>
  label: string
}) {
  const { icon: Icon, label } = props
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-button bg-secondary px-2 py-3 text-center">
      <Icon className="size-4 text-foreground-secondary" />
      <p className="text-label-12 text-foreground">{label}</p>
    </div>
  )
}

function PreviewRow(props: { label: string; value: string }) {
  const { label, value } = props
  return (
    <div className="flex items-center justify-between gap-3 text-label-13">
      <span className="text-foreground-secondary">{label}</span>
      <span className="text-foreground tabular-nums">{value}</span>
    </div>
  )
}

function DocsLink() {
  return (
    <a
      className={cx(linkClassName, 'group flex w-fit items-center gap-3 px-1 py-1 text-label-13')}
      href="https://docs.tempo.xyz"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className="inline-flex items-center gap-1">
        Read the Tempo docs
        <ArrowUpRight className="size-3.5" />
      </span>
    </a>
  )
}

function WelcomeFooter() {
  return (
    <footer className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-center gap-2 px-4 py-6 text-label-13 text-foreground-secondary sm:px-8">
      <a
        className={linkClassName}
        href="https://tempo.xyz/"
        rel="noopener noreferrer"
        target="_blank"
      >
        Tempo
      </a>
      <span aria-hidden>·</span>
      <FooterLink href="/support/privacy-policy">Privacy Policy</FooterLink>
      <span aria-hidden>·</span>
      <FooterLink href="/support/terms-of-service">Terms of Service</FooterLink>
      <span aria-hidden>·</span>
      <FooterLink href="/support">Support</FooterLink>
    </footer>
  )
}

function FooterLink(props: { children: ReactNode; href: string }) {
  const { children, href } = props
  return (
    <a className={linkClassName} href={href}>
      {children}
    </a>
  )
}
